import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MEMBERSHIP_CATEGORIES } from '../../constants';
import { submitApplication, transformFormDataToAPI } from '../../services/api';
import { 
  UserIcon, 
  BriefcaseIcon, 
  AcademicCapIcon, 
  ShieldCheckIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

interface FormData {
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    phoneCountryCode: string;
    dateOfBirth: string;
    nationality: string;
    address: string;
    city: string;
    state: string;
    country: string;
  };
  professionalInfo: {
    currentPosition: string;
    company: string;
    yearsOfExperience: number;
    education: string;
    certifications: string[];
    areasOfExpertise: string[];
  };
  membershipDetails: {
    selectedCategory: string;
    motivation: string;
    previousMemberships: string;
    references: Array<{
      name: string;
      email: string;
      position: string;
      company: string;
    }>;
  };
  agreement: {
    codeOfEthicsAccepted: boolean;
    termsAccepted: boolean;
    dataProcessingAccepted: boolean;
  };
}

const MembershipApplicationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState<FormData>({
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      phoneCountryCode: '+234',
      dateOfBirth: '',
      nationality: 'Nigeria',
      address: '',
      city: '',
      state: '',
      country: 'Nigeria',
    },
    professionalInfo: {
      currentPosition: '',
      company: '',
      yearsOfExperience: 0,
      education: '',
      certifications: [''],
      areasOfExpertise: [''],
    },
    membershipDetails: {
      selectedCategory: '',
      motivation: '',
      previousMemberships: '',
      references: [
        {
          name: '',
          email: '',
          position: '',
          company: '',
        },
      ],
    },
    agreement: {
      codeOfEthicsAccepted: false,
      termsAccepted: false,
      dataProcessingAccepted: false,
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handle pre-selected category from URL
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam && MEMBERSHIP_CATEGORIES.find(cat => cat.id === categoryParam)) {
      updateFormData('membershipDetails', 'selectedCategory', categoryParam);
    }
  }, [searchParams]);

  // Country list for dropdowns
  const countries = [
    { code: 'NG', name: 'Nigeria' },
    { code: 'US', name: 'United States' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'CA', name: 'Canada' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'GH', name: 'Ghana' },
    { code: 'KE', name: 'Kenya' },
    { code: 'UG', name: 'Uganda' },
    { code: 'TZ', name: 'Tanzania' },
    { code: 'RW', name: 'Rwanda' },
    { code: 'ET', name: 'Ethiopia' },
    { code: 'MA', name: 'Morocco' },
    { code: 'EG', name: 'Egypt' },
    { code: 'TN', name: 'Tunisia' },
    { code: 'DZ', name: 'Algeria' },
    { code: 'LY', name: 'Libya' },
    { code: 'SD', name: 'Sudan' },
    { code: 'CM', name: 'Cameroon' },
    { code: 'CI', name: 'Ivory Coast' },
    { code: 'SN', name: 'Senegal' },
    { code: 'ML', name: 'Mali' },
    { code: 'BF', name: 'Burkina Faso' },
    { code: 'NE', name: 'Niger' },
    { code: 'TD', name: 'Chad' },
    { code: 'CF', name: 'Central African Republic' },
    { code: 'AO', name: 'Angola' },
    { code: 'MZ', name: 'Mozambique' },
    { code: 'ZW', name: 'Zimbabwe' },
    { code: 'BW', name: 'Botswana' },
    { code: 'NA', name: 'Namibia' },
    { code: 'SZ', name: 'Eswatini' },
    { code: 'LS', name: 'Lesotho' },
    { code: 'MW', name: 'Malawi' },
    { code: 'ZM', name: 'Zambia' },
    { code: 'MG', name: 'Madagascar' },
    { code: 'MU', name: 'Mauritius' },
    { code: 'SC', name: 'Seychelles' },
    { code: 'CV', name: 'Cape Verde' },
    { code: 'ST', name: 'São Tomé and Príncipe' },
    { code: 'GQ', name: 'Equatorial Guinea' },
    { code: 'GA', name: 'Gabon' },
    { code: 'CG', name: 'Republic of the Congo' },
    { code: 'CD', name: 'Democratic Republic of the Congo' },
    { code: 'BI', name: 'Burundi' },
    { code: 'ER', name: 'Eritrea' },
    { code: 'DJ', name: 'Djibouti' },
    { code: 'SO', name: 'Somalia' },
    { code: 'KM', name: 'Comoros' },
    { code: 'SL', name: 'Sierra Leone' },
    { code: 'LR', name: 'Liberia' },
    { code: 'GN', name: 'Guinea' },
    { code: 'GW', name: 'Guinea-Bissau' },
    { code: 'GM', name: 'Gambia' },
    { code: 'MR', name: 'Mauritania' },
    { code: 'TG', name: 'Togo' },
    { code: 'BJ', name: 'Benin' },
  ];

  // Country code list for phone numbers
  const countryCodes = [
    { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
    { code: '+1', country: 'United States/Canada', flag: '🇺🇸' },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+27', country: 'South Africa', flag: '🇿🇦' },
    { code: '+233', country: 'Ghana', flag: '🇬🇭' },
    { code: '+254', country: 'Kenya', flag: '🇰🇪' },
    { code: '+256', country: 'Uganda', flag: '🇺🇬' },
    { code: '+255', country: 'Tanzania', flag: '🇹🇿' },
    { code: '+250', country: 'Rwanda', flag: '🇷🇼' },
    { code: '+251', country: 'Ethiopia', flag: '🇪🇹' },
    { code: '+212', country: 'Morocco', flag: '🇲🇦' },
    { code: '+20', country: 'Egypt', flag: '🇪🇬' },
    { code: '+216', country: 'Tunisia', flag: '🇹🇳' },
    { code: '+213', country: 'Algeria', flag: '🇩🇿' },
    { code: '+218', country: 'Libya', flag: '🇱🇾' },
    { code: '+249', country: 'Sudan', flag: '🇸🇩' },
    { code: '+237', country: 'Cameroon', flag: '🇨🇲' },
    { code: '+225', country: 'Ivory Coast', flag: '🇨🇮' },
    { code: '+221', country: 'Senegal', flag: '🇸🇳' },
    { code: '+223', country: 'Mali', flag: '🇲🇱' },
    { code: '+226', country: 'Burkina Faso', flag: '🇧🇫' },
    { code: '+227', country: 'Niger', flag: '🇳🇪' },
    { code: '+235', country: 'Chad', flag: '🇹🇩' },
    { code: '+236', country: 'Central African Republic', flag: '🇨🇫' },
    { code: '+244', country: 'Angola', flag: '🇦🇴' },
    { code: '+258', country: 'Mozambique', flag: '🇲🇿' },
    { code: '+263', country: 'Zimbabwe', flag: '🇿🇼' },
    { code: '+267', country: 'Botswana', flag: '🇧🇼' },
    { code: '+264', country: 'Namibia', flag: '🇳🇦' },
    { code: '+268', country: 'Eswatini', flag: '🇸🇿' },
    { code: '+266', country: 'Lesotho', flag: '🇱🇸' },
    { code: '+265', country: 'Malawi', flag: '🇲🇼' },
    { code: '+260', country: 'Zambia', flag: '🇿🇲' },
    { code: '+261', country: 'Madagascar', flag: '🇲🇬' },
    { code: '+230', country: 'Mauritius', flag: '🇲🇺' },
    { code: '+248', country: 'Seychelles', flag: '🇸🇨' },
    { code: '+238', country: 'Cape Verde', flag: '🇨🇻' },
    { code: '+239', country: 'São Tomé and Príncipe', flag: '🇸🇹' },
    { code: '+240', country: 'Equatorial Guinea', flag: '🇬🇶' },
    { code: '+241', country: 'Gabon', flag: '🇬🇦' },
    { code: '+242', country: 'Republic of the Congo', flag: '🇨🇬' },
    { code: '+243', country: 'Democratic Republic of the Congo', flag: '🇨🇩' },
    { code: '+257', country: 'Burundi', flag: '🇧🇮' },
    { code: '+291', country: 'Eritrea', flag: '🇪🇷' },
    { code: '+253', country: 'Djibouti', flag: '🇩🇯' },
    { code: '+252', country: 'Somalia', flag: '🇸🇴' },
    { code: '+269', country: 'Comoros', flag: '🇰🇲' },
    { code: '+232', country: 'Sierra Leone', flag: '🇸🇱' },
    { code: '+231', country: 'Liberia', flag: '🇱🇷' },
    { code: '+224', country: 'Guinea', flag: '🇬🇳' },
    { code: '+245', country: 'Guinea-Bissau', flag: '🇬🇼' },
    { code: '+220', country: 'Gambia', flag: '🇬🇲' },
    { code: '+222', country: 'Mauritania', flag: '🇲🇷' },
    { code: '+228', country: 'Togo', flag: '🇹🇬' },
    { code: '+229', country: 'Benin', flag: '🇧🇯' },
  ];

  const steps = [
    { id: 1, name: 'Personal Information', icon: UserIcon },
    { id: 2, name: 'Professional Information', icon: BriefcaseIcon },
    { id: 3, name: 'Membership Details', icon: AcademicCapIcon },
    { id: 4, name: 'Agreement', icon: ShieldCheckIcon },
  ];

  const updateFormData = (section: keyof FormData, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.personalInfo.firstName) newErrors.firstName = 'First name is required';
      if (!formData.personalInfo.lastName) newErrors.lastName = 'Last name is required';
      if (!formData.personalInfo.email) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.personalInfo.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData.personalInfo.phone) newErrors.phone = 'Phone number is required';
      if (!formData.personalInfo.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      if (!formData.personalInfo.nationality) newErrors.nationality = 'Nationality is required';
    }

    if (step === 2) {
      if (!formData.professionalInfo.currentPosition) newErrors.currentPosition = 'Current position is required';
      if (!formData.professionalInfo.company) newErrors.company = 'Company is required';
      if (formData.professionalInfo.yearsOfExperience < 0) newErrors.yearsOfExperience = 'Years of experience must be valid';
    }

    if (step === 3) {
      if (!formData.membershipDetails.selectedCategory) newErrors.selectedCategory = 'Please select a membership category';
      if (!formData.membershipDetails.motivation) newErrors.motivation = 'Motivation is required';
    }

    if (step === 4) {
      if (!formData.agreement.codeOfEthicsAccepted) newErrors.codeOfEthicsAccepted = 'You must accept the code of ethics';
      if (!formData.agreement.termsAccepted) newErrors.termsAccepted = 'You must accept the terms and conditions';
      if (!formData.agreement.dataProcessingAccepted) newErrors.dataProcessingAccepted = 'You must accept data processing';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const apiData = transformFormDataToAPI(formData);
      const result = await submitApplication(apiData);
      
      if (result.success && result.data) {
        // Redirect to success page with application ID
        navigate(`/membership/application-success?applicationId=${result.data.applicationId}`);
      } else {
        setSubmitError(result.message || 'Failed to submit application');
      }
    } catch (error: any) {
      console.error('Submission error:', error);
      setSubmitError(error.message || 'An error occurred while submitting your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addCertification = () => {
    setFormData(prev => ({
      ...prev,
      professionalInfo: {
        ...prev.professionalInfo,
        certifications: [...prev.professionalInfo.certifications, ''],
      },
    }));
  };

  const addAreaOfExpertise = () => {
    setFormData(prev => ({
      ...prev,
      professionalInfo: {
        ...prev.professionalInfo,
        areasOfExpertise: [...prev.professionalInfo.areasOfExpertise, ''],
      },
    }));
  };

  const addReference = () => {
    setFormData(prev => ({
      ...prev,
      membershipDetails: {
        ...prev.membershipDetails,
        references: [
          ...prev.membershipDetails.references,
          { name: '', email: '', position: '', company: '' },
        ],
      },
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container-max py-6 sm:py-8 lg:py-12">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Membership Application
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Join CSEAN and become part of Nigeria's premier cybersecurity community
            </p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="container-max">
          <div className="py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
          {/* Mobile Progress - Compact view */}
          <div className="block sm:hidden">
            <div className="flex items-center justify-between mb-4">
              {steps.map((step, index) => (
                <div key={step.id} className="flex flex-col items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                    currentStep >= step.id
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'border-gray-300 text-gray-400'
                  }`}>
                    <step.icon className="w-4 h-4" />
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-8 h-0.5 mt-4 ${
                      currentStep > step.id ? 'bg-primary-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-900">
                Step {currentStep} of {steps.length}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {steps[currentStep - 1]?.name}
              </p>
            </div>
          </div>

          {/* Desktop Progress - Full view */}
          <div className="hidden sm:flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  currentStep >= step.id
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'border-gray-300 text-gray-400'
                }`}>
                  <step.icon className="w-5 h-5" />
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    currentStep >= step.id ? 'text-primary-600' : 'text-gray-400'
                  }`}>
                    {step.name}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-12 h-0.5 mx-4 ${
                    currentStep > step.id ? 'bg-primary-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="section-padding">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:p-8">
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Personal Information</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={formData.personalInfo.firstName}
                        onChange={(e) => updateFormData('personalInfo', 'firstName', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                          errors.firstName ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={formData.personalInfo.lastName}
                        onChange={(e) => updateFormData('personalInfo', 'lastName', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                          errors.lastName ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={formData.personalInfo.email}
                        onChange={(e) => updateFormData('personalInfo', 'email', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                      )}
                    </div>

                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-0">
                        <select
                          value={formData.personalInfo.phoneCountryCode}
                          onChange={(e) => updateFormData('personalInfo', 'phoneCountryCode', e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-lg sm:rounded-l-lg sm:rounded-r-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-sm"
                        >
                          {countryCodes.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.flag} {country.code}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          value={formData.personalInfo.phone}
                          onChange={(e) => updateFormData('personalInfo', 'phone', e.target.value)}
                          className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg sm:rounded-l-none sm:rounded-r-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                            errors.phone ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Phone number"
                        />
                      </div>
                      {errors.phone && (
                        <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date of Birth *
                      </label>
                      <input
                        type="date"
                        value={formData.personalInfo.dateOfBirth}
                        onChange={(e) => updateFormData('personalInfo', 'dateOfBirth', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                          errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.dateOfBirth && (
                        <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nationality *
                      </label>
                      <select
                        value={formData.personalInfo.nationality}
                        onChange={(e) => updateFormData('personalInfo', 'nationality', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white ${
                          errors.nationality ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        {countries.map((country) => (
                          <option key={country.code} value={country.name}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                      {errors.nationality && (
                        <p className="mt-1 text-sm text-red-600">{errors.nationality}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <textarea
                      value={formData.personalInfo.address}
                      onChange={(e) => updateFormData('personalInfo', 'address', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City
                      </label>
                      <input
                        type="text"
                        value={formData.personalInfo.city}
                        onChange={(e) => updateFormData('personalInfo', 'city', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State
                      </label>
                      <input
                        type="text"
                        value={formData.personalInfo.state}
                        onChange={(e) => updateFormData('personalInfo', 'state', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country
                      </label>
                      <select
                        value={formData.personalInfo.country}
                        onChange={(e) => updateFormData('personalInfo', 'country', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                      >
                        {countries.map((country) => (
                          <option key={country.code} value={country.name}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Professional Information */}
              {currentStep === 2 && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Professional Information</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Position *
                      </label>
                      <input
                        type="text"
                        value={formData.professionalInfo.currentPosition}
                        onChange={(e) => updateFormData('professionalInfo', 'currentPosition', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                          errors.currentPosition ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.currentPosition && (
                        <p className="mt-1 text-sm text-red-600">{errors.currentPosition}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company *
                      </label>
                      <input
                        type="text"
                        value={formData.professionalInfo.company}
                        onChange={(e) => updateFormData('professionalInfo', 'company', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                          errors.company ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.company && (
                        <p className="mt-1 text-sm text-red-600">{errors.company}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Years of Experience
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.professionalInfo.yearsOfExperience}
                        onChange={(e) => updateFormData('professionalInfo', 'yearsOfExperience', parseInt(e.target.value) || 0)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                          errors.yearsOfExperience ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.yearsOfExperience && (
                        <p className="mt-1 text-sm text-red-600">{errors.yearsOfExperience}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Education
                      </label>
                      <input
                        type="text"
                        value={formData.professionalInfo.education}
                        onChange={(e) => updateFormData('professionalInfo', 'education', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Certifications
                    </label>
                    {formData.professionalInfo.certifications.map((cert, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={cert}
                          onChange={(e) => {
                            const newCerts = [...formData.professionalInfo.certifications];
                            newCerts[index] = e.target.value;
                            updateFormData('professionalInfo', 'certifications', newCerts);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Certification name"
                        />
                        {formData.professionalInfo.certifications.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newCerts = formData.professionalInfo.certifications.filter((_, i) => i !== index);
                              updateFormData('professionalInfo', 'certifications', newCerts);
                            }}
                            className="px-3 py-2 text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addCertification}
                      className="text-primary-600 hover:text-primary-800 text-sm"
                    >
                      + Add Certification
                    </button>
                  </div>

                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Areas of Expertise
                    </label>
                    {formData.professionalInfo.areasOfExpertise.map((area, index) => (
                      <div key={index} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={area}
                          onChange={(e) => {
                            const newAreas = [...formData.professionalInfo.areasOfExpertise];
                            newAreas[index] = e.target.value;
                            updateFormData('professionalInfo', 'areasOfExpertise', newAreas);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Area of expertise"
                        />
                        {formData.professionalInfo.areasOfExpertise.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newAreas = formData.professionalInfo.areasOfExpertise.filter((_, i) => i !== index);
                              updateFormData('professionalInfo', 'areasOfExpertise', newAreas);
                            }}
                            className="px-3 py-2 text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addAreaOfExpertise}
                      className="text-primary-600 hover:text-primary-800 text-sm"
                    >
                      + Add Area of Expertise
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Membership Details */}
              {currentStep === 3 && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Membership Details</h2>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Membership Category *
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {MEMBERSHIP_CATEGORIES.map((category) => (
                        <div
                          key={category.id}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            formData.membershipDetails.selectedCategory === category.id
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-300 hover:border-primary-300'
                          }`}
                          onClick={() => updateFormData('membershipDetails', 'selectedCategory', category.id)}
                        >
                          <h3 className="font-semibold text-gray-900 mb-2">{category.name}</h3>
                          <p className="text-sm text-gray-600 mb-2">{category.description}</p>
                          <div className="text-center">
                            <p className="text-lg font-bold text-primary-600">
                              {category.currency} {category.price.toLocaleString()} to join
                            </p>
                            {category.renewalPrice && (
                              <p className="text-xs text-gray-500">
                                Renewal: {category.currency} {category.renewalPrice.toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {errors.selectedCategory && (
                      <p className="mt-1 text-sm text-red-600">{errors.selectedCategory}</p>
                    )}
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Motivation for Joining CSEAN *
                    </label>
                    <textarea
                      value={formData.membershipDetails.motivation}
                      onChange={(e) => updateFormData('membershipDetails', 'motivation', e.target.value)}
                      rows={4}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        errors.motivation ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Please explain your motivation for joining CSEAN and how you plan to contribute to the cybersecurity community..."
                    />
                    {errors.motivation && (
                      <p className="mt-1 text-sm text-red-600">{errors.motivation}</p>
                    )}
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Previous Professional Memberships
                    </label>
                    <textarea
                      value={formData.membershipDetails.previousMemberships}
                      onChange={(e) => updateFormData('membershipDetails', 'previousMemberships', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="List any previous professional memberships or associations..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Professional References
                    </label>
                    {formData.membershipDetails.references.map((ref, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Name
                            </label>
                            <input
                              type="text"
                              value={ref.name}
                              onChange={(e) => {
                                const newRefs = [...formData.membershipDetails.references];
                                newRefs[index].name = e.target.value;
                                updateFormData('membershipDetails', 'references', newRefs);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Email
                            </label>
                            <input
                              type="email"
                              value={ref.email}
                              onChange={(e) => {
                                const newRefs = [...formData.membershipDetails.references];
                                newRefs[index].email = e.target.value;
                                updateFormData('membershipDetails', 'references', newRefs);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Position
                            </label>
                            <input
                              type="text"
                              value={ref.position}
                              onChange={(e) => {
                                const newRefs = [...formData.membershipDetails.references];
                                newRefs[index].position = e.target.value;
                                updateFormData('membershipDetails', 'references', newRefs);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Company
                            </label>
                            <input
                              type="text"
                              value={ref.company}
                              onChange={(e) => {
                                const newRefs = [...formData.membershipDetails.references];
                                newRefs[index].company = e.target.value;
                                updateFormData('membershipDetails', 'references', newRefs);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                        </div>
                        {formData.membershipDetails.references.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newRefs = formData.membershipDetails.references.filter((_, i) => i !== index);
                              updateFormData('membershipDetails', 'references', newRefs);
                            }}
                            className="mt-2 text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove Reference
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addReference}
                      className="text-primary-600 hover:text-primary-800 text-sm"
                    >
                      + Add Reference
                    </button>
                  </div>
                </div>
              )}

              {/* Step 4: Agreement */}
              {currentStep === 4 && (
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Agreement & Terms</h2>
                  
                  <div className="space-y-6">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="codeOfEthics"
                        checked={formData.agreement.codeOfEthicsAccepted}
                        onChange={(e) => updateFormData('agreement', 'codeOfEthicsAccepted', e.target.checked)}
                        className={`mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded ${
                          errors.codeOfEthicsAccepted ? 'border-red-500' : ''
                        }`}
                      />
                      <div className="ml-3">
                        <label htmlFor="codeOfEthics" className="text-sm font-medium text-gray-700">
                          I agree to abide by the CSEAN Code of Ethics *
                        </label>
                        <p className="text-sm text-gray-500">
                          I commit to maintaining the highest standards of professional conduct and ethical behavior.
                        </p>
                        <a
                          href="/membership/code-of-ethics"
                          className="text-primary-600 hover:text-primary-800 text-sm"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Code of Ethics
                        </a>
                        {errors.codeOfEthicsAccepted && (
                          <p className="mt-1 text-sm text-red-600">{errors.codeOfEthicsAccepted}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="terms"
                        checked={formData.agreement.termsAccepted}
                        onChange={(e) => updateFormData('agreement', 'termsAccepted', e.target.checked)}
                        className={`mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded ${
                          errors.termsAccepted ? 'border-red-500' : ''
                        }`}
                      />
                      <div className="ml-3">
                        <label htmlFor="terms" className="text-sm font-medium text-gray-700">
                          I accept the Terms and Conditions *
                        </label>
                        <p className="text-sm text-gray-500">
                          I have read and agree to the membership terms and conditions.
                        </p>
                        {errors.termsAccepted && (
                          <p className="mt-1 text-sm text-red-600">{errors.termsAccepted}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="dataProcessing"
                        checked={formData.agreement.dataProcessingAccepted}
                        onChange={(e) => updateFormData('agreement', 'dataProcessingAccepted', e.target.checked)}
                        className={`mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded ${
                          errors.dataProcessingAccepted ? 'border-red-500' : ''
                        }`}
                      />
                      <div className="ml-3">
                        <label htmlFor="dataProcessing" className="text-sm font-medium text-gray-700">
                          I consent to data processing *
                        </label>
                        <p className="text-sm text-gray-500">
                          I consent to CSEAN processing my personal data for membership purposes and communications.
                        </p>
                        {errors.dataProcessingAccepted && (
                          <p className="mt-1 text-sm text-red-600">{errors.dataProcessingAccepted}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-4 bg-primary-50 rounded-lg border border-primary-200">
                    <div className="flex items-start">
                      <CheckCircleIcon className="w-6 h-6 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h3 className="text-sm font-semibold text-primary-900 mb-1">
                          Application Review Process
                        </h3>
                        <p className="text-sm text-primary-700">
                          Your application will be reviewed by our membership committee within 5-7 business days. 
                          You will receive an email notification once your application has been processed.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {submitError && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex">
                    <XCircleIcon className="w-5 h-5 text-red-400 mr-3 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-medium text-red-800">Submission Error</h3>
                      <p className="text-sm text-red-700 mt-1">{submitError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex flex-col sm:flex-row justify-between gap-4 mt-6 sm:mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className={`flex items-center justify-center px-4 sm:px-6 py-3 rounded-lg font-medium transition-colors ${
                    currentStep === 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <ArrowLeftIcon className="w-4 h-4 mr-2" />
                  Previous
                </button>

                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn-primary flex items-center justify-center px-4 sm:px-6 py-3"
                  >
                    Next
                    <ArrowRightIcon className="w-4 h-4 ml-2" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="btn-primary flex items-center justify-center px-4 sm:px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <CheckCircleIcon className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipApplicationPage;
